import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { PROPERTY_TYPES } from "@/api/listings";
import type { MarketData } from "@/api/markets";
import { useMarketLink } from "./links";
import { useMarket } from "./MarketProvider";
import { useIsMobile, useV3 } from "./useV3";
import { Pin, Search } from "./Icons";

/* The hero search bar, from the design's search-bar component.
 *
 * DESKTOP is four cells and a button; MOBILE is a location box, a row of two,
 * and a full-width button with no rooms cell. Both are the design's; the
 * viewport picks one.
 *
 * THE LOCATION FIELD IS A COMBOBOX, not free text. The design binds it to
 * `market.cities`, and a search that accepts any typing needs a text search
 * the backend does not have -- so it offers the cities and areas of THIS
 * market and resolves the choice to an id. Typing something it does not know
 * does not silently search for nothing: the field says so on submit, per the
 * no-silent-disabled law. Nothing here is ever disabled-and-quiet.
 *
 * THE PROJECTS TAB IS NOT DRAWN. The design has three tabs -- Buy, Rent,
 * Projects -- and there is no projects backend. A tab that selects a filter
 * nothing can answer is the same fake affordance as a nav item that 404s.
 * Logged in docs/DEVIATIONS.md; it returns with the backend.
 */

type Purpose = "Sale" | "Rent";
type Place = { kind: "city" | "district"; id: number; label: string };

export function SearchBar({ market }: { market: MarketData | null }) {
  const mobile = useIsMobile();
  const { s, ar } = useV3();
  const navigate = useNavigate();
  const link = useMarketLink();
  const { market: active } = useMarket();

  const [purpose, setPurpose] = useState<Purpose>("Sale");
  const [text, setText] = useState("");
  const [place, setPlace] = useState<Place | null>(null);
  const [type, setType] = useState<string | null>(null);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [beds, setBeds] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  /* Cities and districts of the market being browsed, in the active script.
     A district carries its city so "Maadi" and "Maadi, Cairo" are not two
     indistinguishable rows in the list. */
  const places = useMemo<Place[]>(() => {
    if (!market) return [];
    const name = (r: { nameAr: string; nameEn: string }) =>
      ar ? r.nameAr || r.nameEn : r.nameEn || r.nameAr;
    const countryId = active
      ? market.countries.find((c) => c.id === active.id)?.id ?? null
      : null;
    const cities = market.cities.filter((c) => countryId == null || c.countryId === countryId);
    const cityById = new Map(cities.map((c) => [c.id, c]));
    const out: Place[] = cities.map((c) => ({ kind: "city", id: c.id, label: name(c) }));
    for (const d of market.districts) {
      /* A district whose city is not in this market -- or has no city at all
         -- is not offered: picking it would search another country. */
      const city = d.cityId == null ? undefined : cityById.get(d.cityId);
      if (!city) continue;
      out.push({
        kind: "district", id: d.id,
        label: ar ? `${name(d)}، ${name(city)}` : `${name(d)}, ${name(city)}`,
      });
    }
    return out;
  }, [market, ar, active]);

  const matches = useMemo(() => {
    const q = text.trim().toLowerCase();
    if (!q) return places.slice(0, 8);
    return places.filter((p) => p.label.toLowerCase().includes(q)).slice(0, 8);
  }, [places, text]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    /* A typed location that matched nothing is the one case where the search
       would quietly do something other than what was asked. Say so instead. */
    if (text.trim() && !place) {
      const exact = places.find((p) => p.label.toLowerCase() === text.trim().toLowerCase());
      if (!exact) { setProblem(s.hero.locationUnknown); return; }
      setPlace(exact);
    }
    const q = new URLSearchParams();
    q.set("for", purpose);
    const chosen = place ?? places.find((p) => p.label.toLowerCase() === text.trim().toLowerCase());
    if (chosen) q.set(chosen.kind === "city" ? "city" : "district", String(chosen.id));
    if (type) q.set("type", type);
    if (minPrice.trim()) q.set("min", minPrice.trim());
    if (maxPrice.trim()) q.set("max", maxPrice.trim());
    if (beds != null) q.set("beds", String(beds));
    navigate(link(`/search?${q.toString()}`));
  };

  const typeLabel = type ? (s.types[type] ?? type) : s.hero.typeAny;
  const priceLabel = minPrice || maxPrice
    ? [minPrice || "…", maxPrice || "…"].join(" – ")
    : s.hero.priceAny;
  const bedsLabel = beds == null ? s.hero.roomsAny : `${beds}+`;

  const location = (
    <LocationField
      text={text} setText={(v) => { setText(v); setPlace(null); setProblem(null); }}
      matches={matches} onPick={(p) => { setPlace(p); setText(p.label); setProblem(null); }}
      problem={problem} mobile={mobile}
    />
  );
  const typeMenu = (
    <Popover label={s.hero.type} value={typeLabel} mobile={mobile}>
      {(close) => (
        <>
          <button type="button" aria-selected={type === null}
            onClick={() => { setType(null); close(); }}>{s.hero.typeAny}</button>
          {PROPERTY_TYPES.map((ty) => (
            <button key={ty} type="button" aria-selected={type === ty}
              onClick={() => { setType(ty); close(); }}>{s.types[ty] ?? ty}</button>
          ))}
        </>
      )}
    </Popover>
  );
  const priceMenu = (
    <Popover label={s.hero.price} value={priceLabel} mobile={mobile} dialog>
      {() => (
        <div className="sq-popover__price">
          <label className="sq-field">
            <span className="sq-field__hint">{s.hero.priceMin}</span>
            <input className="sq-input sq-input--ltr" inputMode="numeric" value={minPrice}
              onChange={(e) => setMinPrice(e.target.value.replace(/[^\d]/g, ""))} />
          </label>
          <label className="sq-field">
            <span className="sq-field__hint">{s.hero.priceMax}</span>
            <input className="sq-input sq-input--ltr" inputMode="numeric" value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value.replace(/[^\d]/g, ""))} />
          </label>
        </div>
      )}
    </Popover>
  );
  const roomsMenu = (
    <Popover label={s.hero.rooms} value={bedsLabel} mobile={mobile}>
      {(close) => (
        <>
          <button type="button" aria-selected={beds === null}
            onClick={() => { setBeds(null); close(); }}>{s.hero.roomsAny}</button>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" aria-selected={beds === n}
              onClick={() => { setBeds(n); close(); }}>{n}+</button>
          ))}
        </>
      )}
    </Popover>
  );

  return (
    <>
      <div data-component="tabs" className="sq-tabs sq-tabs--hero" role="tablist">
        <button type="button" className="sq-tab" role="tab"
          aria-selected={purpose === "Sale"} onClick={() => setPurpose("Sale")}>
          {s.hero.tabBuy}
        </button>
        <button type="button" className="sq-tab" role="tab"
          aria-selected={purpose === "Rent"} onClick={() => setPurpose("Rent")}>
          {s.hero.tabRent}
        </button>
      </div>
      <form data-component="search-bar" role="search" onSubmit={submit}
        className={mobile ? "sq-search sq-search--mobile" : "sq-search"}>
        {mobile ? (
          <>
            {location}
            <div className="sq-search__row">{typeMenu}{priceMenu}</div>
            <button type="submit" className="sq-btn sq-btn--navy sq-btn--block">
              {s.hero.search}
            </button>
          </>
        ) : (
          <>
            {location}{typeMenu}{priceMenu}{roomsMenu}
            <button type="submit" className="sq-btn sq-btn--navy sq-btn--xl">
              <Search />{s.hero.search}
            </button>
          </>
        )}
      </form>
    </>
  );
}

function LocationField({
  text, setText, matches, onPick, problem, mobile,
}: {
  text: string; setText: (v: string) => void; matches: Place[];
  onPick: (p: Place) => void; problem: string | null; mobile: boolean;
}) {
  const { s } = useV3();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const input = (
    <input
      className={mobile ? "sq-input" : "sq-search__value"}
      style={mobile
        ? { border: 0, padding: 0, height: "auto" }
        : { border: 0, padding: 0, outlineOffset: 6 }}
      placeholder={s.hero.locationPlaceholder}
      name="q" autoComplete="off" role="combobox"
      aria-expanded={open} aria-autocomplete="list"
      aria-invalid={problem ? true : undefined}
      value={text}
      onChange={(e) => { setText(e.target.value); setOpen(true); }}
      onFocus={() => setOpen(true)}
    />
  );

  return (
    <div ref={wrap} className="sq-search__field">
      <label className={mobile ? "sq-search__box sq-search__box--lg" : "sq-search__cell"}>
        {mobile ? <Pin /> : <span className="sq-search__label">{s.hero.location}</span>}
        {input}
      </label>
      {open && matches.length > 0 && (
        <div className="sq-popover" role="listbox">
          {matches.map((p) => (
            <button key={`${p.kind}-${p.id}`} type="button"
              onClick={() => { onPick(p); setOpen(false); }}>{p.label}</button>
          ))}
        </div>
      )}
      {/* Explains itself on attempt, inline, where the mistake is. */}
      {problem && <p className="sq-field__error" role="alert">{problem}</p>}
    </div>
  );
}

/* A search cell that opens something. The design draws the closed cell; the
 * open panel is app-owned and reuses one popover look across the page. */
function Popover({
  label, value, mobile, dialog, children,
}: {
  label: string; value: string; mobile: boolean; dialog?: boolean;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); button.current?.focus(); }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrap} className="sq-search__field">
      <button ref={button} type="button"
        className={mobile ? "sq-search__box" : "sq-search__cell"}
        aria-haspopup={dialog ? "dialog" : "listbox"} aria-expanded={open}
        onClick={() => setOpen((o) => !o)}>
        {!mobile && <span className="sq-search__label">{label}</span>}
        <span className="sq-search__value">{value}</span>
      </button>
      {open && (
        <div className="sq-popover" role={dialog ? "dialog" : "listbox"} aria-label={label}>
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}
